import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useService } from '@aesop-fables/containr-react';
import { OrganizationApi } from '../api/apis/OrganizationApi';
import { ApiKeys } from '../api/apis/ApiKeys';
import { OrganizationData } from '../models/OrganizationData';
import { Toast } from '../models/Toast';

export function useOrgSearch({
  setToast,
  trimmedSearch,
  isSearching,
}: {
  setToast: (toast: Toast) => void;
  trimmedSearch: string;
  isSearching: boolean;
}): { orgResults: OrganizationData[]; orgSearchLoading: boolean } {
  const organizationApi = useService<OrganizationApi>(ApiKeys.Organization);

  const [orgResults, setOrgResults] = useState<OrganizationData[]>([]);
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const searchOrgs = useCallback(
    async (query: string) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setOrgSearchLoading(true);
      try {
        const response = await organizationApi.search(query, 0, controller.signal);
        setOrgResults(response.data.content);
      } catch {
        if (controller.signal.aborted) return;
        setToast(
          new Toast({ message: 'Error searching organizations', severity: 'error', open: true }),
        );
      } finally {
        if (!controller.signal.aborted) setOrgSearchLoading(false);
      }
    },
    [organizationApi, setToast],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchOrgs = useCallback(debounce(searchOrgs, 500), [searchOrgs]);

  useEffect(() => {
    return () => {
      debouncedSearchOrgs.cancel();
      abortControllerRef.current?.abort();
    };
  }, [debouncedSearchOrgs]);

  useEffect(() => {
    if (!isSearching) {
      abortControllerRef.current?.abort();
      setOrgResults([]);
      setOrgSearchLoading(false);
      debouncedSearchOrgs.cancel();
      return;
    }

    setOrgSearchLoading(true);
    debouncedSearchOrgs(trimmedSearch);
  }, [trimmedSearch, isSearching, debouncedSearchOrgs]);

  return { orgResults, orgSearchLoading };
}
